'use client';

import React, { useEffect, useRef } from 'react';
import { ContentfulComponentProps } from '../type';
import { responsiveBreakpointsPx } from '@/constants';

const STICKY_CTA_DISMISSED_KEY = 'stickyCtaDismissed';

export function StickyCtaTrigger({ asset }: ContentfulComponentProps<SiteStickyCta>): React.JSX.Element {
    const triggerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const navEntries = performance.getEntriesByType?.('navigation');
        const navEntry = navEntries?.[0] as PerformanceNavigationTiming | undefined;
        if (navEntry?.type === 'reload' || navEntry?.type === 'back_forward') {
            sessionStorage.removeItem(STICKY_CTA_DISMISSED_KEY);
            document.body.classList.remove('stickyBarStick');
        }
    }, []);

    useEffect(() => {
        const isGlobalNavBehavior = asset.fields.presentation.styles['Scroll-up Behavior'] === 'Global Nav';

        function updateOffsets() {
            const nav = document.querySelector('[data-componenttype="Global Nav"]') as HTMLElement;
            const stickyBar = document.querySelector('[class*="stickybar"]') as HTMLElement;

            const isMobile = window.innerWidth < responsiveBreakpointsPx['md'];
            const isSticking = document.body.classList.contains('stickyBarStick');
            const stickyH = isSticking ? (stickyBar?.offsetHeight ?? 0) : 0;
            const navH = nav?.offsetHeight ?? 0;
            document.documentElement.style.setProperty('--sticky-bar-top', `${isMobile ? 0 : stickyH}px`);

            let channelGuideTop: number;

            if (isMobile) {
                if (isGlobalNavBehavior && !isSticking) {
                    channelGuideTop = navH;
                } else {
                    channelGuideTop = 0;
                }
            } else {
                if (isGlobalNavBehavior) {
                    const navRect = nav?.getBoundingClientRect();
                    const navBottom = navRect ? Math.max(navRect.bottom, 0) : 0;
                    channelGuideTop = isSticking ? stickyH : navBottom;
                } else {
                    const isAtPageTop = window.scrollY < 10;
                    channelGuideTop = isAtPageTop ? stickyH + navH : stickyH;
                }
            }
            document.documentElement.style.setProperty('--channel-guide-top', `${channelGuideTop}px`);
        }

        updateOffsets();

        const nav = document.querySelector('[data-componenttype="Global Nav"]') as HTMLElement;
        const ro = new ResizeObserver(updateOffsets);
        if (nav) ro.observe(nav);

        const mo = new MutationObserver(updateOffsets);
        mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        window.addEventListener('scroll', updateOffsets, { passive: true });
        window.addEventListener('resize', updateOffsets);

        return () => {
            ro.disconnect();
            mo.disconnect();
            window.removeEventListener('scroll', updateOffsets);
            window.removeEventListener('resize', updateOffsets);
        };
    }, [asset.fields.presentation.styles]);

    useEffect(() => {
        let lastScroll = 0;

        function scrollHandler() {
            if (sessionStorage.getItem(STICKY_CTA_DISMISSED_KEY)) {
                document.body.classList.remove('stickyBarStick');
                return;
            }

            const top = triggerRef?.current?.getBoundingClientRect()?.top;
            const dir = lastScroll < window.scrollY ? 'down' : 'up';
            lastScroll = window.scrollY;

            const scrollPosition = window.innerHeight + window.scrollY;
            const bottomPosition = document.body.offsetHeight - 100;

            if (scrollPosition >= bottomPosition) {
                document.body.classList.remove('stickyBarStick');
                return;
            }

            if (asset.fields.presentation.styles['Scroll-up Behavior'] === 'Global Nav' && dir === 'up') {
                document.body.classList.remove('stickyBarStick');
            } else if (top !== undefined) {
                if (top <= -1) {
                    document.body.classList.add('stickyBarStick');
                } else {
                    document.body.classList.remove('stickyBarStick');
                }
            }
        }

        document.addEventListener('scroll', scrollHandler);
        return () => document.removeEventListener('scroll', scrollHandler);
    }, [asset.fields.presentation.styles]);

    return <div ref={triggerRef} data-note="sticky-trigger"></div>;
}
